import founderApi, { type ApiResponse } from "../services/founderApi";
import type { FounderAuditLogEntry, FounderSecurityAlertEntry } from "../services/founderActivityLabels";
import type { FounderScannerHealth, FounderWorkspaceCounts } from "../services/founderHomeDashboard";

export type FounderApplicationRow = {
  id: string;
  name: string;
  slug: string;
  status: string;
  created_at: string;
  published_at: string | null;
  publisher_name: string;
  category_name: string | null;
};

export type FounderPublisherRow = {
  id: string;
  email: string;
  full_name: string;
  account_status: string;
  display_name: string;
  published_applications: string;
  agreement_version: string | null;
};

export type FounderCategoryRow = {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  display_order: number;
  is_active: boolean;
  archived_at: string | null;
};

export type FounderMembershipPlanRow = Record<string, unknown> & {
  id: string;
  name: string;
  slug: string;
  price_amount: string | number;
  currency: string;
  renewal_increase_percent: string | number;
  is_active: boolean;
};

export type FounderMembershipPricingHistoryRow = {
  id: string;
  previousPrice: number | null;
  newPrice: number;
  effectiveDate: string | null;
  changedAt: string;
  changeType: "created" | "price_update";
};

export type FounderPlatformSettings = {
  publicationMode: string;
  settings: Array<{
    setting_key: string;
    setting_value: unknown;
    description: string | null;
    updated_at: string;
  }>;
};

export async function fetchFounderWorkspaceCounts(): Promise<FounderWorkspaceCounts> {
  const response = await founderApi.get<ApiResponse<{ counts: FounderWorkspaceCounts }>>(
    "/founder/workspace",
  );
  return response.data.data.counts;
}

export async function fetchFounderApplications(
  status?: string,
  query?: string,
): Promise<FounderApplicationRow[]> {
  const response = await founderApi.get<ApiResponse<{ applications: FounderApplicationRow[] }>>(
    "/founder/applications",
    {
      params: {
        ...(status ? { status } : {}),
        ...(query ? { q: query } : {}),
      },
    },
  );
  return response.data.data.applications;
}

export type FounderApplicationDetail = {
  application: Record<string, unknown>;
  securityScans: Array<Record<string, unknown>>;
  statusHistory: Array<Record<string, unknown>>;
  reviewReadiness: {
    canApprove: boolean;
    securityStatus: string;
    publicReleaseAllowed: boolean;
    latestScanVerdict: string | null;
    latestScanReason: string | null;
    blockingReason: string | null;
  };
};

export async function fetchFounderApplicationDetail(
  applicationId: string,
): Promise<FounderApplicationDetail> {
  const response = await founderApi.get<ApiResponse<FounderApplicationDetail>>(
    `/founder/applications/${applicationId}/detail`,
  );
  return response.data.data;
}

export type FounderPublisherDetail = {
  publisher: Record<string, unknown>;
  applications: Array<Record<string, unknown>>;
  subscriptions: Array<Record<string, unknown>>;
  payments: Array<Record<string, unknown>>;
  history: Array<Record<string, unknown>>;
};

export async function fetchFounderPublisherDetail(
  publisherProfileId: string,
): Promise<FounderPublisherDetail> {
  const response = await founderApi.get<ApiResponse<FounderPublisherDetail>>(
    `/founder/publishers/${publisherProfileId}/detail`,
  );
  return response.data.data;
}

export async function createFounderMembershipPlan(input: {
  name: string;
  slug?: string;
  description?: string;
  priceAmount: number;
  currency?: string;
  billingPeriodDays?: number;
  renewalIncreasePercent?: number;
  isActive?: boolean;
  confirmPassword: string;
}): Promise<FounderMembershipPlanRow> {
  const response = await founderApi.post<ApiResponse<{ plan: FounderMembershipPlanRow }>>(
    "/founder/platform/membership-plans",
    input,
  );
  return response.data.data.plan;
}

export async function publishFounderMembershipPlanPricing(
  planId: string,
  input: {
    priceAmount?: number;
    renewalIncreasePercent?: number;
    effectiveDate: string;
    applyScope: "new_memberships_only" | "new_and_renewals";
    reason?: string;
    confirmPassword: string;
  },
): Promise<{ mode: "immediate" | "scheduled"; scheduleId?: string }> {
  const response = await founderApi.post<
    ApiResponse<{ mode: "immediate" | "scheduled"; scheduleId?: string }>
  >(`/founder/platform/membership-plans/${planId}/pricing`, input);
  return response.data.data;
}

export type FounderPriceScheduleRow = {
  id: string;
  price_amount: string | number;
  renewal_increase_percent: string | number | null;
  apply_scope: string;
  effective_date: string;
  status: string;
  created_at: string;
  applied_at: string | null;
  cancelled_at: string | null;
  cancellation_reason: string | null;
};

export async function fetchFounderMembershipPlanPriceSchedules(
  planId: string,
): Promise<FounderPriceScheduleRow[]> {
  const response = await founderApi.get<ApiResponse<{ schedules: FounderPriceScheduleRow[] }>>(
    `/founder/platform/membership-plans/${planId}/price-schedules`,
  );
  return response.data.data.schedules;
}

export async function cancelFounderMembershipPlanPriceSchedule(
  scheduleId: string,
  reason?: string,
): Promise<void> {
  await founderApi.patch(`/founder/platform/membership-plans/price-schedules/${scheduleId}/cancel`, {
    reason,
  });
}

export async function interveneFounderSubscription(
  subscriptionId: string,
  input: {
    action: "extend" | "correct_expiry";
    days?: number;
    expiresAt?: string;
    reason: string;
  },
): Promise<{ expiresAt: string | null }> {
  const response = await founderApi.patch<ApiResponse<{ expiresAt: string | null }>>(
    `/founder/subscriptions/${subscriptionId}/intervene`,
    input,
  );
  return response.data.data;
}

export type FounderLegalDocumentRow = {
  id: string;
  document_type: string;
  version: string;
  title: string;
  effective_date: string;
  is_current: boolean;
  created_at: string;
};

export async function fetchFounderLegalDocuments(): Promise<FounderLegalDocumentRow[]> {
  const response = await founderApi.get<ApiResponse<{ documents: FounderLegalDocumentRow[] }>>(
    "/founder/platform/legal-documents",
  );
  return response.data.data.documents;
}

export async function createFounderLegalDocument(input: {
  documentType: "publisher_agreement" | "privacy_policy" | "disclaimer";
  version: string;
  title: string;
  content: string;
  effectiveDate: string;
  confirmPassword: string;
}): Promise<void> {
  await founderApi.post("/founder/platform/legal-documents", input);
}

export async function resolveFounderSecurityAlert(
  alertId: string,
  note: string,
): Promise<void> {
  await founderApi.patch(`/founder/security/alerts/${alertId}/resolve`, { note });
}

export type FounderOperationalHealth = {
  databaseStatus: string;
  paymentGatewayStatus: string;
  scannerSummary: string;
  failedPayments: number;
  labels: Record<string, string>;
};

export async function fetchFounderOperationalHealth(): Promise<FounderOperationalHealth> {
  const response = await founderApi.get<ApiResponse<{ health: FounderOperationalHealth }>>(
    "/founder/platform/health",
  );
  return response.data.data.health;
}

export type FounderPublisherPaymentProviderStatus = {
  displayName: string;
  status: "active" | "unavailable";
  available: boolean;
};

export async function fetchFounderPublisherPaymentProviderStatus(): Promise<{
  razorpay: FounderPublisherPaymentProviderStatus;
}> {
  const response = await founderApi.get<
    ApiResponse<{ razorpay: FounderPublisherPaymentProviderStatus }>
  >("/founder/platform/publisher-payment-provider-status");
  return response.data.data;
}

export type FounderMaintenanceMode = {
  enabled: boolean;
  message: string;
};

export async function fetchFounderMaintenanceMode(): Promise<FounderMaintenanceMode> {
  const response = await founderApi.get<ApiResponse<{ maintenance: FounderMaintenanceMode }>>(
    "/founder/platform/maintenance",
  );
  return response.data.data.maintenance;
}

export async function updateFounderMaintenanceMode(input: {
  enabled: boolean;
  message: string;
  reason?: string;
  confirmPassword: string;
}): Promise<FounderMaintenanceMode> {
  const response = await founderApi.patch<ApiResponse<{ maintenance: FounderMaintenanceMode }>>(
    "/founder/platform/maintenance",
    input,
  );
  return response.data.data.maintenance;
}

export type OperationalTemplate = {
  label: string;
  subject: string;
  body: string;
  deliveryConnected: boolean;
};

export async function fetchFounderOperationalTemplates(): Promise<
  Record<string, OperationalTemplate>
> {
  const response = await founderApi.get<ApiResponse<{ templates: Record<string, OperationalTemplate> }>>(
    "/founder/platform/operational-templates",
  );
  return response.data.data.templates;
}

export async function updateFounderOperationalTemplate(
  templateKey: string,
  input: { subject: string; body: string },
): Promise<OperationalTemplate> {
  const response = await founderApi.patch<ApiResponse<{ template: OperationalTemplate }>>(
    `/founder/platform/operational-templates/${templateKey}`,
    input,
  );
  return response.data.data.template;
}

export async function changeFounderPassword(input: {
  currentPassword: string;
  newPassword: string;
}): Promise<void> {
  await founderApi.post("/auth/founder/change-password", input);
}

export async function fetchFounderMfaStatus(): Promise<{ enabled: boolean; statusLabel: string }> {
  const response = await founderApi.get<ApiResponse<{ enabled: boolean; statusLabel: string }>>(
    "/auth/founder/mfa/status",
  );
  return response.data.data;
}

export async function beginFounderMfaSetup(): Promise<{
  qrCodeDataUrl: string;
  manualEntryHint: string;
}> {
  const response = await founderApi.post<
    ApiResponse<{ qrCodeDataUrl: string; manualEntryHint: string }>
  >("/auth/founder/mfa/setup");
  return response.data.data;
}

export async function activateFounderMfa(verificationCode: string): Promise<{ recoveryCodes: string[] }> {
  const response = await founderApi.post<ApiResponse<{ recoveryCodes: string[] }>>(
    "/auth/founder/mfa/activate",
    { verificationCode },
  );
  return response.data.data;
}

export async function disableFounderMfa(input: {
  password: string;
  verificationCode?: string;
  recoveryCode?: string;
}): Promise<void> {
  await founderApi.post("/auth/founder/mfa/disable", input);
}

export async function regenerateFounderRecoveryCodes(password: string): Promise<{ recoveryCodes: string[] }> {
  const response = await founderApi.post<ApiResponse<{ recoveryCodes: string[] }>>(
    "/auth/founder/mfa/recovery/regenerate",
    { password },
  );
  return response.data.data;
}

export type FounderReviewDecision =
  | "approve"
  | "request_revision"
  | "reject"
  | "suspend"
  | "restore"
  | "delist";

export async function fetchFounderApplicationById(
  applicationId: string,
): Promise<FounderApplicationRow | null> {
  const applications = await fetchFounderApplications();
  return applications.find((app) => app.id === applicationId) ?? null;
}

export async function submitFounderApplicationReview(
  applicationId: string,
  decision: FounderReviewDecision,
  notes?: string,
): Promise<string> {
  const response = await founderApi.post<ApiResponse<{ status: string }>>(
    `/founder/applications/${applicationId}/review`,
    { decision, notes },
  );
  return response.data.data.status;
}

export async function fetchFounderPublishers(): Promise<FounderPublisherRow[]> {
  const response = await founderApi.get<ApiResponse<{ publishers: FounderPublisherRow[] }>>(
    "/founder/publishers",
  );
  return response.data.data.publishers;
}

export async function updateFounderPublisherStatus(
  publisherProfileId: string,
  status: "active" | "suspended",
): Promise<void> {
  await founderApi.patch(`/founder/publishers/${publisherProfileId}/status`, { status });
}

export async function fetchFounderCategories(): Promise<FounderCategoryRow[]> {
  const response = await founderApi.get<ApiResponse<{ categories: FounderCategoryRow[] }>>(
    "/founder/platform/categories",
  );
  return response.data.data.categories;
}

export async function createFounderCategory(input: {
  name: string;
  description?: string;
  displayOrder?: number;
}): Promise<void> {
  await founderApi.post("/founder/platform/categories", {
    name: input.name,
    description: input.description,
    displayOrder: input.displayOrder ?? 0,
  });
}

export async function updateFounderCategory(
  categoryId: string,
  input: {
    name?: string;
    description?: string;
    displayOrder?: number;
    isActive?: boolean;
    archive?: boolean;
  },
): Promise<FounderCategoryRow> {
  const response = await founderApi.patch<ApiResponse<{ category: FounderCategoryRow }>>(
    `/founder/platform/categories/${categoryId}`,
    input,
  );
  return response.data.data.category;
}

export async function fetchFounderMembershipPlans(): Promise<FounderMembershipPlanRow[]> {
  const response = await founderApi.get<ApiResponse<{ plans: FounderMembershipPlanRow[] }>>(
    "/founder/platform/membership-plans",
  );
  return response.data.data.plans;
}

export async function updateFounderMembershipPlan(
  planId: string,
  input: {
    name?: string;
    description?: string;
    priceAmount?: number;
    currency?: string;
    renewalIncreasePercent?: number;
    isActive?: boolean;
  },
): Promise<FounderMembershipPlanRow> {
  const response = await founderApi.patch<ApiResponse<{ plan: FounderMembershipPlanRow }>>(
    `/founder/platform/membership-plans/${planId}`,
    input,
  );
  return response.data.data.plan;
}

export async function fetchFounderMembershipPlanPricingHistory(
  planId: string,
): Promise<FounderMembershipPricingHistoryRow[]> {
  const response = await founderApi.get<
    ApiResponse<{ history: FounderMembershipPricingHistoryRow[] }>
  >(`/founder/platform/membership-plans/${planId}/pricing-history`);
  return response.data.data.history;
}

export type FounderSubscriptionRow = {
  id: string;
  status: string;
  amount_paid: string | number | null;
  currency: string;
  starts_at: string | null;
  expires_at: string | null;
  renewal_count: number;
  created_at: string;
  application_id: string;
  application_name: string;
  publisher_name: string;
  plan_name: string;
};

export type FounderPaymentRow = {
  id: string;
  amount: string | number;
  currency: string;
  status: string;
  provider: string;
  provider_reference: string | null;
  provider_order_id: string | null;
  payment_purpose: string | null;
  created_at: string;
  verified_at: string | null;
  application_name: string | null;
  publisher_name: string;
  publisher_email: string | null;
  plan_name: string | null;
  membership_status: string | null;
  membership_starts_at: string | null;
  membership_expires_at: string | null;
  renewal_count: number | null;
};

export type FounderPaymentSummary = {
  succeededCount: number;
  pendingCount: number;
  failedCount: number;
  refundedCount: number;
  cancelledCount: number;
  membershipRevenue: number;
  renewalRevenue: number;
  currency: string;
};

export async function fetchFounderSubscriptions(
  status?: string,
): Promise<FounderSubscriptionRow[]> {
  const response = await founderApi.get<ApiResponse<{ subscriptions: FounderSubscriptionRow[] }>>(
    "/founder/subscriptions",
    status ? { params: { status } } : undefined,
  );
  return response.data.data.subscriptions;
}

export async function fetchFounderPayments(filters?: {
  query?: string;
  status?: string;
  provider?: string;
  from?: string;
  to?: string;
}): Promise<FounderPaymentRow[]> {
  const response = await founderApi.get<ApiResponse<{ payments: FounderPaymentRow[] }>>(
    "/founder/payments",
    {
      params: {
        ...(filters?.query ? { q: filters.query } : {}),
        ...(filters?.status && filters.status !== "all" ? { status: filters.status } : {}),
        ...(filters?.provider && filters.provider !== "all" ? { provider: filters.provider } : {}),
        ...(filters?.from ? { from: filters.from } : {}),
        ...(filters?.to ? { to: filters.to } : {}),
        limit: 300,
      },
    },
  );
  return response.data.data.payments;
}

export async function fetchFounderPaymentSummary(): Promise<FounderPaymentSummary> {
  const response = await founderApi.get<ApiResponse<{ summary: FounderPaymentSummary }>>(
    "/founder/payments/summary",
  );
  return response.data.data.summary;
}

export async function fetchFounderPaymentDetail(paymentId: string): Promise<FounderPaymentRow> {
  const response = await founderApi.get<ApiResponse<{ payment: FounderPaymentRow }>>(
    `/founder/payments/${paymentId}`,
  );
  return response.data.data.payment;
}

export type FounderTrafficSummary = {
  recordingStartedAt: string | null;
  uniqueVisitorDefinition: string;
  visitorsToday: number;
  visitorsLast7Days: number;
  visitorsLast30Days: number;
  totalRecordedVisitors: number;
  pageViewsToday: number;
  pageViewsLast7Days: number;
  pageViewsLast30Days: number;
  totalRecordedPageViews: number;
  botPageViewsLast30Days: number;
  topPages: Array<{ path: string; views: number }>;
  devices: Array<{ name: string; count: number }>;
  browsers: Array<{ name: string; count: number }>;
  platforms: Array<{ name: string; count: number }>;
  countries: Array<{ name: string; count: number }>;
  trend7Days: Array<{ day: string; visitors: number; pageViews: number }>;
  trend30Days: Array<{ day: string; visitors: number; pageViews: number }>;
};

export async function fetchFounderTrafficSummary(): Promise<FounderTrafficSummary> {
  const response = await founderApi.get<ApiResponse<{ traffic: FounderTrafficSummary }>>(
    "/founder/traffic",
  );
  return response.data.data.traffic;
}

export async function fetchFounderPlatformSettings(): Promise<FounderPlatformSettings> {
  const response = await founderApi.get<ApiResponse<FounderPlatformSettings>>(
    "/founder/platform/settings",
  );
  return response.data.data;
}

export async function updateFounderPublicationMode(
  mode: "review_required" | "instant",
  confirmPassword: string,
): Promise<void> {
  await founderApi.patch("/founder/platform/settings/publication-mode", { mode, confirmPassword });
}

export async function fetchFounderAuditLogs(limit = 50): Promise<FounderAuditLogEntry[]> {
  const response = await founderApi.get<ApiResponse<{ logs: FounderAuditLogEntry[] }>>(
    "/founder/audit-logs",
    { params: { limit } },
  );
  return response.data.data.logs;
}

export async function fetchFounderSecurityAlerts(
  limit = 50,
): Promise<FounderSecurityAlertEntry[]> {
  const response = await founderApi.get<ApiResponse<{ alerts: FounderSecurityAlertEntry[] }>>(
    "/founder/security/alerts",
    { params: { limit, resolved: "false" } },
  );
  return response.data.data.alerts;
}

export async function fetchFounderScanners(): Promise<FounderScannerHealth[]> {
  const response = await founderApi.get<ApiResponse<{ scanners: FounderScannerHealth[] }>>(
    "/founder/security/scanners",
  );
  return response.data.data.scanners;
}
